import React from 'react';
import PreLoanFormV3 from './PreLoanForm';
import { useQuery } from '../../../utils/helpers';
import { Origin, Partner } from '../../../components/LeadFormIndex/types';
import { getComplementInviteContext } from '../../../queries/complementInvite';
import analytics, { resetAnalytics } from '../../../utils/analytics';
import {
  INVITEE_CATEGORY,
  INVITEE_DEAD_LINK_PAGE,
  INVITEE_RATE_LIMIT_PAGE,
  INVITEE_WELCOME_PAGE,
} from './ComplementInvite/analyticsPages';
import InviteUnavailable, { InviteLoading } from './ComplementInvite/InviteUnavailable';

type PreLoanRequestV3Props = {
    preLoanId: string,
    handleShowForm: (show:boolean)=> void,
    origin: Origin
    partner: Partner
    onPlainLayout?: (step: number) => void,
}

type InviteState = 'loading' | 'ok' | 'dead' | 'rateLimited';

export default function PreLoanRequestIndexV3(props:PreLoanRequestV3Props) {
  const {
    preLoanId, handleShowForm, origin, partner, onPlainLayout,
  } = props;
  const query = useQuery();
  const [preLoanRut, setPreLoanRut] = React.useState('');
  const [amount, setAmount] = React.useState('');
  const [periods, setPeriods] = React.useState('');
  const [installment, setInstallment] = React.useState('');
  const [referralCode, setReferralCode] = React.useState<string|null>(null);
  const [complementToken, setComplementToken] = React.useState<string|null>(null);
  const [complementLeadFirstName, setComplementLeadFirstName] = React.useState<string|undefined>();
  const [optIn, setOptIn] = React.useState(false);

  const phone = query.get('phone');
  const complementTokenFromQuery = query.get('complementToken');
  // Invitado al complemento: sin pantalla de bienvenida. Se lee la invitación (nombre de quien
  // invita y si sigue vigente) y el paso del RUT se adapta; si no sirve, se avisa en el shell.
  const [inviteState, setInviteState] = React.useState<InviteState>(
    complementTokenFromQuery ? 'loading' : 'ok',
  );

  React.useEffect(() => {
    const rut = query.get('rut');
    const amountData = query.get('amount');
    const periodsData = query.get('periods');
    const installmentData = query.get('installment');
    const referralCodeData = query.get('referralCode');
    const complementTokenData = query.get('complementToken');
    const optInData = query.get('optIn');
    setAmount(amountData || '');
    setPeriods(periodsData || '');
    setInstallment(installmentData || '');
    setPreLoanRut(rut || '');
    setReferralCode(referralCodeData);
    setComplementToken(complementTokenData);
    setOptIn(optInData === 'true');
  }, [query]);

  React.useEffect(() => {
    if (!complementTokenFromQuery) return;
    // Mismos eventos que la bienvenida anterior (page/pages/complement-invite).
    resetAnalytics();
    getComplementInviteContext(complementTokenFromQuery)
      .then((invite) => {
        if (!invite.data?.eligible) {
          analytics.page(INVITEE_CATEGORY, INVITEE_DEAD_LINK_PAGE);
          setInviteState('dead');
          return;
        }
        analytics.page(INVITEE_CATEGORY, INVITEE_WELCOME_PAGE);
        setComplementLeadFirstName(invite.data.leadFirstName);
        setInviteState('ok');
        handleShowForm(true);
      })
      .catch((e) => {
        if (e === 429) {
          analytics.page(INVITEE_CATEGORY, INVITEE_RATE_LIMIT_PAGE);
          setInviteState('rateLimited');
        } else {
          analytics.page(INVITEE_CATEGORY, INVITEE_DEAD_LINK_PAGE);
          setInviteState('dead');
        }
      });
  }, [complementTokenFromQuery]);

  if (inviteState === 'loading') return <InviteLoading />;
  if (inviteState === 'dead' || inviteState === 'rateLimited') {
    return <InviteUnavailable partner={partner} rateLimited={inviteState === 'rateLimited'} />;
  }

  return (
    <PreLoanFormV3
      preLoanRut={preLoanRut}
      phone={phone || '+56'}
      preLoanRequestId={preLoanId}
      origin={origin}
      partner={partner}
      amount={amount}
      periods={periods}
      installment={installment}
      referralCode={referralCode}
      complementToken={complementToken ?? complementTokenFromQuery}
      complementLeadFirstName={complementLeadFirstName}
      optIn={optIn}
      onErrorReset={() => handleShowForm(false)}
      onPlainLayout={onPlainLayout}
    />
  );
}

PreLoanRequestIndexV3.defaultProps = {
  onPlainLayout: undefined,
};
